import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Typography } from '../../interfaces/typography';

interface Option {
  value: string;
  label: string;
}

interface SimplifiedSingleSelectProps {
  options: Option[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
}

export function SimplifiedSingleSelect({ options, value, onChange, placeholder = 'Select an option...' }: SimplifiedSingleSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      const option = filteredOptions[focusedIndex];
      onChange(option.value);
      setIsOpen(false);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className="border rounded-lg bg-white px-2 py-1.5 h-[38px] cursor-pointer flex items-center"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        <Typography 
          variant="mediumParagraph" 
          color={selectedOption ? '#111827' : '#6B7280'} 
          className="truncate flex-1"
        >
          {selectedOption ? selectedOption.label : placeholder}
        </Typography>
        <ChevronDown size={18} className="shrink-0 text-gray-400 ml-2" />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60">
          <div className="p-1.5 border-b">
            <div className="relative">
              <Search size={18} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                className="w-full pl-7 pr-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search options..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  fontSize: '13px',
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
                }}
              />
            </div>
          </div>
          <div className="max-h-40 overflow-auto">
            {filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`px-2 py-1.5 cursor-pointer flex items-center gap-2 ${
                  focusedIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                <div className={`shrink-0 w-4 h-4 border rounded-full flex items-center justify-center ${
                  value === option.value ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                }`}>
                  {value === option.value && <Check size={12} className="text-white" />}
                </div>
                <Typography variant="mediumParagraph" color="#111827" className="truncate">
                  {option.label}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}